import { Injectable } from '@angular/core'
import { Socket } from 'ngx-socket-io'
import { MessageService } from '@openng/optimus-ui/api'
import { RaidenContext, RaidenSidebar, SDWANInstallationEvent, SDWANMigrationEvent, SDWANSpeedtestEvent, fqNotification, fqSettings } from './fuseqRaiden.model'
import { BehaviorSubject, Observable, merge } from 'rxjs'
import { map } from 'rxjs/operators'
import { Router } from '@angular/router'
import { HttpClient } from '@angular/common/http'
import { FuseqHeaders } from '../fuseq.headers'
import { environment } from '../../../environments/environment'

/**
 * Tipi spediti dal backend con `fuseqRaiden.ship('newEvent', ...)`.
 * Il server Raiden non ri-emette un canale `newEvent`: usa come nome evento
 * `event.type` e come payload `event.content` (vedi awp-node/fuseqRaiden.js,
 * `io.emit(event.type, event.content)`). Per riceverli bisogna quindi
 * ascoltare il singolo tipo.
 */
const NEW_EVENT_TYPES = ['MAINTENANCE_UPDATED', 'SUPPORT_TICKET_CREATED', 'SUPPORT_TICKET_UPDATED'] as const

@Injectable({
  providedIn: 'root'
})

export class FuseqRaidenService {
  subscriptions = {}
  private currentUserId: string | null = null
  private reconnectAttempts = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private readonly raidenContext = new BehaviorSubject<RaidenContext | null>(null)
  private readonly activeContext = new BehaviorSubject<any>(null)
  private readonly roomClients = new BehaviorSubject<any>(null)
  private readonly raidenClients = new BehaviorSubject<any>(null)
  private addNotification (notification: fqNotification) {
    const severity = notification.type === 'warning' ? 'warn' : notification.type
    this.messageService.add({ severity, summary: notification.title, detail: notification.message })
  }

  constructor (private readonly socket: Socket, private readonly messageService: MessageService, private readonly router: Router, private readonly http: HttpClient, private readonly fuseqHeaders: FuseqHeaders) {
    /* this.socket.fromEvent('settings').subscribe((settings: fqSettings) => {
      if (settings?.subscriptions && settings.subscriptions.length > 0) {
        for (const subscribe of settings.subscriptions) {
          this.subscriptions[subscribe] = this.socket.fromEvent(subscribe)
        }
      }
    }) */
    this.socket.fromEvent('globalNotification').subscribe((notification: fqNotification) => {
      console.log(notification)
      this.addNotification(notification)
    })
    this.socket.fromEvent('privateNotification').subscribe((notification: fqNotification) => {
      console.log(notification)
      this.addNotification(notification)
    })
    this.socket.fromEvent('event:global').subscribe((notification: fqNotification) => {
      console.log(notification)
      this.addNotification(notification)
    })
    this.socket.fromEvent('event:private').subscribe((notification: fqNotification) => {
      console.log(notification)
      this.addNotification(notification)
    })
    this.socket.fromEvent('context:active').subscribe((event: any) => {
      if (event.editors && event.editors.length > 1) {
        const others = event.editors.filter(x => x.socket_id !== this.socket.ioSocket.id)
        console.log(others)
        if (others.length >= 1) {
          this.activeContext.next(event)
          this.messageService.add({ severity: 'warn', summary: 'This record is being edited by:', detail: others.map(x => x.user_id).join(', ') })
        }
      }
    })
    this.socket.fromEvent('clients').subscribe((data: any) => {
      this.raidenClients.next(data)
      let clients = data.rooms[`room:${this.router.url}`]?.clients || []
      if (clients.length > 0) {
        clients = clients.filter(x => x.socket_id !== this.socket.ioSocket.id).map((c: any) => { return { socket_id: c.socket_id, user_id: c.user_id, joined_at: c.joined_at } })
      }
      //  console.log('clients in room', this.router.url, clients)
      this.roomClients.next(clients)
    })
    // Il server puo' chiudere la connessione (deploy, riavvio, timeout lato server).
    // In caso di "io server disconnect" socket.io NON riconnette automaticamente:
    // va richiamata connect() o la pagina resta muta fino al refresh.
    // IMPORTANTE: se manca lo user_id il server disconnette subito il client; senza
    // questo guard la connect() immediata creerebbe un loop infinito che satura la CPU.
    this.socket.fromEvent('disconnect').subscribe((reason: any) => {
      if (reason === 'io client disconnect') {
        return
      }
      if (!this.currentUserId) {
        return
      }
      this.scheduleReconnect()
    })
    this.socket.fromEvent('connect').subscribe(() => {
      this.reconnectAttempts = 0
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
      }
    })
    // invia a socket il messaggio 'clients' in loop ogni 30 secondi senza fermarsi
    setInterval(() => {
      if (this.socket.ioSocket.connected) {
        this.socket.emit('clients')
      }
    }, 3000)
  }

  private scheduleReconnect () {
    if (this.reconnectTimer) {
      return
    }
    const delay = Math.min(1000 * 2 ** this.reconnectAttempts, 30000)
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      if (!this.currentUserId) {
        return
      }
      this.reconnectAttempts++
      this.socket.connect()
    }, delay)
  }

  private _raidenSession: RaidenSidebar[]
  set raidenSession (value) {
    this._raidenSession = value
    sessionStorage.setItem('raidenSession', JSON.stringify(value))
  }

  get raidenSession () {
    const session = (this._raidenSession ? this._raidenSession : JSON.parse(sessionStorage.getItem('raidenSession')))
    console.log('get session', session, this._raidenSession, JSON.parse(sessionStorage.getItem('raidenSession')))
    return session || [
      { name: 'CalendarEvents', active: true, showOptions: false, optionName: null, icon: 'fa fa-calendar', data: [] },
      {
        name: 'SDWANInstallationEvents',
        active: true,
        showOptions: true,
        optionName: 'SDWAN - Installazioni',
        options: [
          { name: 'Sidebar', active: true },
          { name: 'Popup', active: true }
        ],
        icon: 'fa fa-tower-cell',
        data: []
      },
      {
        name: 'SDWANMigrationEvents',
        active: true,
        showOptions: true,
        optionName: 'SDWAN - Migrazioni',
        options: [
          { name: 'Sidebar', active: true },
          { name: 'Popup', active: true }
        ],
        icon: 'fa fa-house-signal',
        data: []
      },
      {
        name: 'RakerEvents',
        active: true,
        showOptions: true,
        optionName: 'AWP - Raker',
        options: [
          { name: 'Sidebar', active: true },
          { name: 'Popup', active: true }
        ],
        icon: 'fa fa-file-import',
        data: []
      },
      {
        name: 'SDWANSpeedtestEvents',
        active: true,
        showOptions: true,
        optionName: 'SDWAN - Speedtest',
        options: [
          { name: 'Sidebar', active: true },
          { name: 'Popup', active: true }
        ],
        icon: 'fa fa-gauge',
        data: []
      },
      { name: 'Settings', active: true, showOptions: false, optionName: null, icon: 'fa fa-gears', data: [] }
    ]
  }

  /* sendGlobalNotification (notification: fqNotification) {
    this.socket.emit('newGlobalNotification', notification)
  } */
  updateUserId (userId: any) {
    if (userId === undefined || userId === null || userId === '') {
      return
    }
    const id = userId.toString()
    this.currentUserId = id
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.reconnectAttempts = 0
    if (this.socket.ioSocket.connected && this.socket.ioSocket.io.opts.query?.user_id === id) {
      return
    }
    this.socket.disconnect()
    this.socket.ioSocket.io.opts.query = { user_id: id }
    this.socket.connect()
  }

  setRaidenContext (action: string, key: string, obj: { id: string, [key: string]: any } | null) {
    const context: RaidenContext = { obj, key, action }
    this.raidenContext.next(context)
    // const room = `edit:${context.url}:${context.key}:${context.id}`
    // this.socket.emit('join-room', room)
  }

  clearRaidenContext () {
    const context = this.raidenContext.value
    if (context) {
      // const room = `edit:${context.url}:${context.key}:${context.id}`
      // this.socket.emit('leave-room', room)
    }
    this.raidenContext.next(null)
  }

  getRaidenContext (): Observable<RaidenContext | null> {
    return this.raidenContext.asObservable()
  }

  getRaidenActiveContext (): Observable<any | null> {
    return this.activeContext.asObservable()
  }

  onSpeedTestEvent () {
    return this.socket.fromEvent('speedTestEvent')
  }

  onActiveTasksEvent () {
    return this.socket.fromEvent('activeTasksList')
  }

  onNRD_DATAEvent () {
    return this.socket.fromEvent('NRD_DATA')
  }

  onSwitch_DATAEvent () {
    return this.socket.fromEvent('SWITCH_DATA')
  }

  onPreStaging_DATAEvent () {
    return this.socket.fromEvent('PRE_STAGING')
  }

  onSystemCron_DATAEvent () {
    return this.socket.fromEvent('SYSTEM_CRON')
  }

  onNRD_MIGREvent () {
    return this.socket.fromEvent('NRD_MIGR')
  }

  onSDWANInstallationEvent () {
    return this.socket.fromEvent<SDWANInstallationEvent>('SDWANInstallationEvent')
  }

  onSDWANMigrationEvent () {
    return this.socket.fromEvent<SDWANMigrationEvent>('SDWANMigrationEvent')
  }

  onSDWANSpeedtestEvent () {
    return this.socket.fromEvent<SDWANSpeedtestEvent>('SDWANSpeedtestEvent')
  }

  /**
   * Eventi spediti dal backend con `newEvent`, normalizzati nell'envelope
   * { type, content } atteso dai consumer.
   *
   * ATTENZIONE: sul server il nome dell'evento socket è `event.type` e il
   * payload è `event.content`; non esiste un canale 'newEvent' (quello è solo
   * il nome del comando client→server). Ascoltare `fromEvent('newEvent')`
   * significava non ricevere mai nulla.
   */
  onNewEvent () {
    return merge(
      ...NEW_EVENT_TYPES.map(type =>
        this.socket.fromEvent<any>(type).pipe(map((content) => ({ type, content })))
      )
    )
  }

  onEventLogger () {
    return this.socket.fromEvent('fuseqEventLogger')
  }

  onEvent (eventName: string) {
    return this.socket.fromEvent(eventName)
  }

  onConnect () {
    return this.socket.fromEvent('connect')
  }

  getRaidenRoomClients () {
    return this.roomClients.asObservable()
  }

  getRaidenClients () {
    return this.raidenClients.asObservable()
  }

  joinRoom (room: object | string) {
    return this.socket.emit('joinRoom', room)
  }

  leaveRoom (room: object | string) {
    return this.socket.emit('leaveRoom', room)
  }

  sendReaction (socket_id: string) {
    this.socket.emit('sendReaction', { socket_id, reaction: '👀', from: this.socket.ioSocket.io.opts.query.user_id })
  }

  receiveReaction () {
    return this.socket.fromEvent('receiveReaction')
  }

  requestDesktopPermission (): void {
    if ('Notification' in window) {
      Notification.requestPermission()
    }
  }

  showDesktopNotification (title: string, options?: NotificationOptions): void {
    if (Notification.permission === 'granted') {
      new Notification(title, options)
    }
  }

  async subscribeToPush () {
    console.log('Subscribing to push notifications...')
    if (!('serviceWorker' in navigator)) {
      console.error('Service workers are not supported in this browser.')
      return
    }
    if (!('PushManager' in window)) {
      console.error('Push messaging is not supported in this browser.')
      return
    }
    if (Notification.permission === 'denied') {
      console.warn('Push notifications are blocked by the user.')
      return
    }
    const reg = await navigator.serviceWorker.ready
    console.log('Service Worker is ready:', reg)
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: this.urlBase64ToUint8Array('BGOBawdeS6tf1ABaxa1I-6E7eOVK_JwJHbNSGMw-sz9tYbZ8T77egmS7tr0tlcNRChqdtaCTnhOAlx-SoGrEapw')
    })
    console.log(sub)
    this.http.post(`${environment.API_URL}/subscribe`, sub, this.fuseqHeaders.setHeaders('POST')).subscribe()
  }

  private urlBase64ToUint8Array (base64: string): Uint8Array<ArrayBuffer> {
    const padding = '='.repeat((4 - base64.length % 4) % 4)
    const base64Clean = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
    const raw = window.atob(base64Clean)
    return new Uint8Array([...raw].map(c => c.charCodeAt(0))) as Uint8Array<ArrayBuffer>
  }
}
